/**
 * `useDeleteApprovalChain` mutation hook.
 *
 * Wraps the raw `deleteApprovalChain` service fn (per AD-23). Invalidates every
 * approval chain query under the project (`['project', projectId, 'approval-chains']`)
 * so the list refreshes after a delete.
 *
 * The BE also drops every assignment of the deleted chain, which the definition
 * keys do not cover — assignments live under the asset-scoped namespace and
 * folder progress under the folder keys. Both are busted here, or an asset keeps
 * rendering the deleted chain with an unassign that 404s.
 *
 * Side effects: NONE. Toast and confirm-dialog UX belong to the caller — the
 * delete returns `{ affectedAssetCount, affectedFolderCount }` which the
 * confirmation dialog surfaces before the call, and the list owns the toast.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/approval-chain';

import { invalidateProjectChainAssignments } from './invalidate-project-chain-assignments';
import { deleteApprovalChain } from './approval-chains.api';

export { deleteApprovalChain };

interface UseDeleteApprovalChainOptions {
  projectId: string;
}

export function useDeleteApprovalChain(options: UseDeleteApprovalChainOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (approvalChainId: string) => deleteApprovalChain(projectId, approvalChainId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_CHAIN_QUERY_KEYS.all(projectId),
      });

      invalidateProjectChainAssignments(queryClient, projectId);
    },
  });
}
